import React from 'react';
import {Outlet, Link, useLocation, useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {logout} from '../store/slices/authSlice';
import {notificationApi} from '../services/notificationApi';
import {clearNotificationState, setNotificationUnreadCount} from '../store/slices/notificationSlice';
import {clearUser} from '../store/slices/userSlice';
import {RootState} from '../store';
import {getAssetUrl, getDisplayInitial} from '../utils/assets';
import HomeFooter from '../pages/main/home/components/HomeFooter';
import './Layout.css';

type NavLinkConfig = {
  to: string;
  label: string;
  icon?: string;
  iconUrl?: string;
};

const homeRoundedIconUrl = `${process.env.PUBLIC_URL}/icons/home-fill.svg`;
const navServicesIconUrl = `${process.env.PUBLIC_URL}/icons/service-fill.svg`;
const profileFillIconUrl = `${process.env.PUBLIC_URL}/icons/profile-fill.svg`;
const messageFillIconUrl = `${process.env.PUBLIC_URL}/icons/message-fill.svg`;
const analyticsFillIconUrl = `${process.env.PUBLIC_URL}/icons/analytics-fill.svg`;
const settingFillIconUrl = `${process.env.PUBLIC_URL}/icons/setting-fill.svg`;
const notificationFillIconUrl = `${process.env.PUBLIC_URL}/icons/notification-fill.svg`;
const adFillIconUrl = `${process.env.PUBLIC_URL}/icons/ad-filled.svg`;
const arrowDownBoldIconUrl = `${process.env.PUBLIC_URL}/icons/arrow-down-bold.svg`;
const logoutFillIconUrl = `${process.env.PUBLIC_URL}/icons/logout-fill.svg`;
const navIconUrls: Partial<Record<string, string>> = {
  '/home': homeRoundedIconUrl,
  '/services': navServicesIconUrl,
  '/profile': profileFillIconUrl,
  '/messages': messageFillIconUrl,
  '/analytics': analyticsFillIconUrl,
  '/settings': settingFillIconUrl,
  '/notifications': notificationFillIconUrl,
  '/advertise': adFillIconUrl,
};

const primaryNavLinks: NavLinkConfig[] = [
  {to: '/home', icon: '🏠', label: 'Home'},
  {to: '/services', icon: '🎯', label: 'Services'},
  {to: '/profile', icon: '👤', label: 'Profile'},
  {to: '/messages', icon: '✉️', label: 'Messages'},
  {to: '/analytics', icon: '📊', label: 'Analytics'},
  {to: '/settings', icon: '⚙️', label: 'Settings'},
];

const mobileNavLinks: NavLinkConfig[] = [
  ...primaryNavLinks,
  {to: '/notifications', icon: '🔔', label: 'Notifications'},
  {to: '/advertise', icon: '📢', label: 'Advertise'},
];

const renderNavIcon = (link: NavLinkConfig) => {
  const iconUrl = link.iconUrl || navIconUrls[link.to];

  if (iconUrl) {
    return (
      <span
        className="nav-icon nav-icon-mask"
        style={{'--nav-icon-url': `url("${iconUrl}")`} as React.CSSProperties}
        aria-hidden="true"
      />
    );
  }

  return <span className="nav-icon" aria-hidden="true">{link.icon}</span>;
};

const renderMenuIcon = (iconUrl: string) => (
  <span
    className="avatar-menu-icon"
    style={{'--avatar-menu-icon-url': `url("${iconUrl}")`} as React.CSSProperties}
    aria-hidden="true"
  />
);

const Layout: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const token = useSelector((state: RootState) => state.auth.token);
  const unreadNotificationCount = useSelector((state: RootState) => state.notifications.unreadCount);
  const user = useSelector((state: RootState) => state.user.currentUser);
  const [avatarImageFailed, setAvatarImageFailed] = React.useState(false);
  const [isAvatarMenuOpen, setIsAvatarMenuOpen] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const avatarMenuRef = React.useRef<HTMLDivElement | null>(null);
  const mobileMenuRef = React.useRef<HTMLDivElement | null>(null);
  const userAvatarUrl = getAssetUrl(user?.avatar);
  const shouldRenderAvatarImage = Boolean(userAvatarUrl) && !avatarImageFailed;

  const syncUnreadNotificationCount = React.useCallback(async () => {
    if (!token) {
      dispatch(clearNotificationState());
      return;
    }

    try {
      const summary = await notificationApi.getUnreadSummary(token);
      dispatch(setNotificationUnreadCount(summary.unreadCount));
    } catch {
      // Keep the last known count if the poll fails.
    }
  }, [dispatch, token]);

  React.useEffect(() => {
    setAvatarImageFailed(false);
  }, [userAvatarUrl]);

  React.useEffect(() => {
    setIsAvatarMenuOpen(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    if (!location.hash) {
      return;
    }

    let targetId = '';

    try {
      targetId = decodeURIComponent(location.hash.slice(1));
    } catch {
      targetId = location.hash.slice(1);
    }

    if (!targetId) {
      return;
    }

    let animationFrameId = 0;
    let retryTimeoutId = 0;

    const scrollToHashTarget = () => {
      const targetElement = document.getElementById(targetId);

      if (!targetElement) {
        return false;
      }

      const navbarHeight = document.querySelector('.navbar')?.getBoundingClientRect().height || 0;
      const targetTop = targetElement.getBoundingClientRect().top + window.scrollY;

      window.scrollTo({
        top: Math.max(0, targetTop - navbarHeight - 16),
        behavior: 'smooth',
      });

      return true;
    };

    animationFrameId = window.requestAnimationFrame(() => {
      if (!scrollToHashTarget()) {
        retryTimeoutId = window.setTimeout(scrollToHashTarget, 100);
      }
    });

    return () => {
      window.cancelAnimationFrame(animationFrameId);

      if (retryTimeoutId) {
        window.clearTimeout(retryTimeoutId);
      }
    };
  }, [location.hash, location.pathname]);

  React.useEffect(() => {
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;

      if (isAvatarMenuOpen && avatarMenuRef.current && !avatarMenuRef.current.contains(target)) {
        setIsAvatarMenuOpen(false);
      }

      if (isMobileMenuOpen && mobileMenuRef.current && !mobileMenuRef.current.contains(target)) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsAvatarMenuOpen(false);
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isAvatarMenuOpen, isMobileMenuOpen]);

  React.useEffect(() => {
    void syncUnreadNotificationCount();

    if (!token) {
      return;
    }

    const handleWindowFocus = () => {
      void syncUnreadNotificationCount();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void syncUnreadNotificationCount();
      }
    };

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void syncUnreadNotificationCount();
      }
    }, 30000);

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [syncUnreadNotificationCount, token]);

  const handleLogout = () => {
    dispatch(logout());
    dispatch(clearNotificationState());
    dispatch(clearUser());
    navigate('/login');
  };

  const handleProfileNavigation = () => {
    setIsAvatarMenuOpen(false);
    navigate('/profile');
  };

  const handleAvatarMenuToggle = () => {
    setIsMobileMenuOpen(false);
    setIsAvatarMenuOpen((previousValue) => !previousValue);
  };

  const handleMobileMenuToggle = () => {
    setIsAvatarMenuOpen(false);
    setIsMobileMenuOpen((previousValue) => !previousValue);
  };

  const notificationBadgeText = unreadNotificationCount > 99 ? '99+' : String(unreadNotificationCount);

  return (
    <div className="layout">
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-main-row">
            <Link to="/home" className="navbar-brand">
              <div className="logo-container">
                <img
                  src={`${process.env.PUBLIC_URL}/xpsr-logo.png`}
                  alt=""
                  className="logo-image"
                  width="48"
                  height="48"
                  aria-hidden="true"
                />
                <div className="logo-text">
                  <span className="logo-xpsr">XPSR</span>
                  <span className="logo-tagline">Express Yourself</span>
                </div>
              </div>
            </Link>

            <div className="navbar-menu">
              {primaryNavLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="navbar-link"
                  title={link.label}
                  aria-label={link.label}
                >
                  {renderNavIcon(link)}
                  <span className="navbar-link-label">{link.label}</span>
                </Link>
              ))}
            </div>

            <div className="navbar-user">
              <Link
                to="/notifications"
                className="navbar-notifications"
                title="Notifications"
                aria-label="Notifications"
              >
                {renderNavIcon({to: '/notifications', label: 'Notifications'})}
                <span className="nav-icon" aria-hidden="true">🔔</span>
                <span className="navbar-link-label">Notifications</span>
                {unreadNotificationCount > 0 ? (
                  <span className="navbar-notification-badge">
                    {notificationBadgeText}
                  </span>
                ) : null}
              </Link>

              <Link
                to="/advertise"
                className="navbar-advertise"
                title="Advertise"
                aria-label="Advertise"
              >
                {renderNavIcon({to: '/advertise', label: 'Advertise'})}
                <span className="nav-icon" aria-hidden="true">📢</span>
                <span className="navbar-advertise-label">Advertise</span>
              </Link>

              <div className="navbar-mobile-menu" ref={mobileMenuRef}>
                <button
                  type="button"
                  className="navbar-menu-toggle"
                  aria-label="Toggle navigation menu"
                  aria-expanded={isMobileMenuOpen}
                  onClick={handleMobileMenuToggle}
                >
                  <span className="navbar-menu-toggle-bar" />
                  <span className="navbar-menu-toggle-bar" />
                  <span className="navbar-menu-toggle-bar" />
                </button>

                {isMobileMenuOpen ? (
                  <div className="navbar-mobile-panel">
                    {mobileNavLinks.map((link) => {
                      const isNotificationLink = link.to === '/notifications';
                      const mobileLinkLabel =
                        isNotificationLink && unreadNotificationCount > 0
                          ? `${link.label}, ${unreadNotificationCount} unread`
                          : link.label;

                      return (
                        <Link
                          key={link.to}
                          to={link.to}
                          className="navbar-mobile-link"
                          aria-label={mobileLinkLabel}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {renderNavIcon(link)}
                          <span>{link.label}</span>
                          {isNotificationLink && unreadNotificationCount > 0 ? (
                            <span className="navbar-mobile-badge">{notificationBadgeText}</span>
                          ) : null}
                        </Link>
                      );
                    })}
                  </div>
                ) : null}
              </div>

              <div className="navbar-avatar-menu" ref={avatarMenuRef}>
                <button
                  type="button"
                  className="user-avatar-button"
                  aria-label={user?.name ? `${user.name} account menu` : 'Open account menu'}
                  aria-expanded={isAvatarMenuOpen}
                  aria-haspopup="menu"
                  title={user?.name || 'Account menu'}
                  onClick={handleAvatarMenuToggle}
                >
                  <div className="user-avatar">
                    {shouldRenderAvatarImage ? (
                      <img
                        src={userAvatarUrl}
                        alt={`${user?.name || 'User'} avatar`}
                        className="user-avatar-image"
                        onError={() => setAvatarImageFailed(true)}
                      />
                    ) : (
                      getDisplayInitial(user?.name)
                    )}
                  </div>
                  <span className="user-name">{user?.name || 'User'}</span>
                  <span
                    className="user-menu-chevron"
                    style={{'--user-menu-chevron-url': `url("${arrowDownBoldIconUrl}")`} as React.CSSProperties}
                    aria-hidden="true"
                  />
                </button>

                {isAvatarMenuOpen ? (
                  <div className="avatar-menu-dropdown" role="menu">
                    <button
                      type="button"
                      className="avatar-menu-action"
                      role="menuitem"
                      onClick={handleProfileNavigation}
                    >
                      {renderMenuIcon(profileFillIconUrl)}
                      <span className="nav-icon">👤</span>
                      <span>Go to Profile</span>
                    </button>
                    <button
                      type="button"
                      className="avatar-menu-action"
                      role="menuitem"
                      onClick={handleLogout}
                    >
                      {renderMenuIcon(logoutFillIconUrl)}
                      <span className="nav-icon">🚪</span>
                      <span>Logout</span>
                    </button>
                  </div>
                ) : null}
              </div>

            </div>
          </div>
        </div>
      </nav>
      
      <main className="main-content">
        <Outlet />
      </main>

      <HomeFooter />
    </div>
  );
};

export default Layout;
